/**
 * NotFoundScreen — catch-all 404 fallback at route path="*".
 *
 * Polished empty state per design system:
 *   - EmptyState atom: warm icon tile (Ic "question"), serif T.title2 headline,
 *     T.subhead supporting text
 *   - PressBtn "На главную" — in-app navigate, terracotta brand colour
 *
 * NO openExternal / BOT_LINK — architectural-integrity §7 bans openExternal
 * as a native action. The only CTA here is in-app navigate.
 *
 * Content is hardcoded inline (no i18n) — per FTR-998 convention.
 *
 * Uses:
 *   - components/atoms: StaticPage, EmptyState, Ic, PressBtn
 *   - hooks/usePlatformBack: TG back-button registration
 *   - react-router-dom: useNavigate
 *
 * Used by:
 *   - App.tsx (catch-all route path="*", last child of <Routes>)
 */
import { useNavigate } from 'react-router-dom';
import { StaticPage, EmptyState, Ic, PressBtn } from '../components/atoms';
import { usePlatformBack } from '../hooks/usePlatformBack';
import { P } from '../styles/tokens';

// ─── NotFoundScreen ───────────────────────────────────────────────────────────

export default function NotFoundScreen() {
  const navigate = useNavigate();
  const onBack = () => navigate(-1);

  // Registers navigate(-1) on the TG BackButton for hardware/OS back gestures.
  usePlatformBack();

  return (
    <StaticPage title="Страница не найдена" onBack={onBack}>
      {/* Warm empty state — icon tile + serif title + supporting text */}
      <EmptyState
        icon={<Ic name="question" size={28} color={P.muted} />}
        title="Упс, такой страницы нет"
        subtitle="Попробуй вернуться на главную или нажми кнопку «Назад»."
      />

      {/* In-app CTA — terracotta brand button, navigate replaces history entry */}
      <div style={{ padding: '24px 0 8px' }}>
        <PressBtn
          bg={P.terracotta}
          onClick={() => navigate('/', { replace: true })}
        >
          На главную
        </PressBtn>
      </div>
    </StaticPage>
  );
}
